import { createSelector } from "reselect"
import { get, chain, map, filter, orderBy, isEmpty, first, find, mergeWith, isNumber, some } from "lodash"
import { createQueryStringSelector } from "selectors/queryString"
import { getTickerAndMicFromContributor } from "helpers/securities"
import { getAllAvailableRollups } from "helpers/rollups"
import { positionSetContributorsSummaryText, sortContributors } from "helpers/contributors"
import { computeFactorCategories } from "helpers/factors"
import { computeGroupedContributorsCSV, computeContributorsGroupCSV, computeContributorsCSV } from "helpers/csv"
import { aggregateFactorsSumBy } from "helpers/factorClassification/index"
import { computeSelectedGroup } from "helpers/analyze"
import { VIEW_TYPES } from "helpers/base"
import { makeGroupSort } from "helpers/contributorsSort"
import { computeAttributionMethod } from "helpers/risk"
import { isCashOffsetContributor } from "helpers/assets"

const EMPTY_ARRAY = []
const EMPTY_OBJECT = {}

const getSelectedGroupId = createQueryStringSelector("contributorGroup")
const getContributorsData = state => get(state, "global.data.compositeRiskContributors.data.model.simulation.riskContributors", EMPTY_ARRAY)
const getGroupedContributorsData = state => get(state, "global.data.compositeGroupedRiskContributors.data.model.simulation.groupedRiskContributors")
const getPositionSetName = (_, props) => props.positionSetName
const getSortOrder = (_, props) => get(props, "sortOrder", EMPTY_OBJECT)
const getCurrentGrouping = (_, props) => get(props, "currentGrouping")
const getClassifications = (_, props) => props.classifications
const getSelectedDate = (_, props) => props.selectedDate
const getSelectedViews = (_, props) => props.selectedViews
const getShowNotHeld = (_, props) => props.showNotHeld
const getViewType = (_, props) => props.viewType
const getMustAggregateFactors = (_, props) => props.mustAggregateFactors
const getFactorsClassification = (_, props) => props.factorsClassification
const getSelectedViewsFactors = (_, props) => props.selectedViewsFactors
export const getGroupBy = (_, props) => get(props, "currentGrouping.id")
export const getSelectedContributorCategory = (_, props) => get(props, "selectedContributorCategory", EMPTY_OBJECT)

function getFactors(factors, mustAggregateFactors, selectedViewsFactors, factorsClassification) {
  if (!mustAggregateFactors) {
    return factors
  }
  const result = aggregateFactorsSumBy(factorsClassification, factors, "value")

  if (isEmpty(selectedViewsFactors)) {
    return result
  }

  return chain(factors)
    .filter(factor => some(selectedViewsFactors, item => item.id === factor.id))
    .concat(result)
    .value()
}

function computeNotHeldBucket(contributors, mustAggregateFactors, selectedViews, selectedViewsFactors, factorsClassification) {
  const merge = (obj, source) => mergeWith(obj, source, (objValue, srcValue) =>
    !objValue ? srcValue : objValue + srcValue,
  )

  return chain(contributors)
    .filter(item => !!item.notHeld)
    .reduce((acc, value) => {
      acc.size += value.percentEquity
      acc.total += value.total
      acc.factorsTotal += value.attribution.summary.factors
      acc.specific += value.attribution.summary.specific

      const factors = getFactors(value.attribution.factors, mustAggregateFactors, selectedViewsFactors, factorsClassification)
      merge(acc.factors, chain(factors)
        .keyBy("id")
        .mapValues(f => f.value)
        .value())

      const factorCategories = computeFactorCategories(factors, selectedViews)
      merge(acc.categories, factorCategories)

      return acc
    }, {
      id: "NOT HELD",
      notHeld: true,
      size: 0,
      total: 0,
      factorsTotal: 0,
      specific: 0,
      factors: {},
      categories: {},
    })
    .value()
}

function mapContributors(contributors, showNotHeld, mustAggregateFactors, selectedViews, selectedViewsFactors, factorsClassification) {
  const result = chain(contributors)
    .filter(contributor => showNotHeld || !contributor.notHeld)
    .map(security => {
      const { ticker, mic: exchange } = getTickerAndMicFromContributor(security)
      const factors = getFactors(security.attribution.factors, mustAggregateFactors, selectedViewsFactors, factorsClassification)

      return {
        id: security.id,
        size: security.percentEquity,
        total: security.total,
        country: security.country,
        classification: get(security, "classification.name"),
        currency: security.currency,
        assetClass: security.assetClass,
        assetSubclass: security.assetSubclass,
        description: security.description,
        notHeld: security.notHeld,
        bloombergTicker: security.bloombergTicker,
        factorsTotal: security.attribution.summary.factors,
        specific: security.attribution.summary.specific,
        modelProviderId: security.modelProviderId,
        factors: chain(factors)
          .keyBy("id")
          .mapValues(f => f.value)
          .value(),
        categories: computeFactorCategories(factors, selectedViews),
        ticker,
        exchange,
      }
    })
    .value()

  if (!showNotHeld) {
    result.push(computeNotHeldBucket(contributors, mustAggregateFactors, selectedViews, selectedViewsFactors, factorsClassification))
  }
  return result
}

export const getSelectedGroup = createSelector(
  [getSelectedGroupId, getGroupedContributorsData],
  (selectedGroupId, contributors) => computeSelectedGroup({ id: selectedGroupId }, contributors),
)

export const getIncludeNotHeld = createSelector(
  [getViewType],
  (viewType) => viewType === VIEW_TYPES.ACTIVE,
)

export const getContributors = createSelector(
  [getContributorsData, getShowNotHeld, getMustAggregateFactors, getSelectedViews, getSelectedViewsFactors, getFactorsClassification],
  mapContributors,
)

const getSelectedField = createSelector(
  [getSelectedContributorCategory],
  (category) => get(category, "field", "total"),
)

const getFilteredContributors = createSelector(
  [getContributors, getSelectedField],
  (data, selectedField) =>
    filter(data, security => get(security, selectedField)),
)

const getSortedContributors = createSelector(
  [getFilteredContributors, getSelectedField, getSortOrder],
  (data, selectedField, order) => {
    if (data.length === 0) return EMPTY_ARRAY

    return orderBy(data, [
      (security) => {
        switch (order.by) {
          case "name":
            return security.id
          case "equity":
            return order.abs ? Math.abs(security.size) : security.size
          default: {
            const value = get(security, selectedField)
            return order.abs && isNumber(value) ? Math.abs(value) : value
          }
        }
      },
    ], [order.dir])
  },
)

export const getSortedContributorsWithoutCashOffset = createSelector(
  [getSortedContributors],
  contributors => filter(contributors, contributor => !isCashOffsetContributor(contributor)),
)

const getGroupedContributors = createSelector(
  [getGroupedContributorsData, getShowNotHeld, getMustAggregateFactors, getSelectedViews, getSelectedViewsFactors, getFactorsClassification],
  (data, showNotHeld, mustAggregateFactors, selectedViews, selectedViewsFactors, factorsClassification) =>
    map(data, group => {
      const factors = getFactors(group.attribution.factors, mustAggregateFactors, selectedViewsFactors, factorsClassification)

      return {
        ...group,
        total: group.attribution.summary.factors + group.attribution.summary.specific,
        factorsTotal: group.attribution.summary.factors,
        specific: group.attribution.summary.specific,
        factors: chain(factors)
          .keyBy("id")
          .mapValues(f => f.value)
          .value(),
        categories: computeFactorCategories(factors, selectedViews),
        contributors: mapContributors(group.contributors, showNotHeld, mustAggregateFactors, selectedViews, selectedViewsFactors, factorsClassification),
      }
    }),
)

const getFilteredGroupedContributors = createSelector(
  [getGroupedContributors, getSelectedField, getSelectedGroup],
  (data, selectedField, selectedGroup) => {
    const groupFilter = selectedGroup ? group => selectedGroup.id === group.id : group => get(group, selectedField)
    return chain(data)
      .filter(groupFilter)
      .map(group => ({
        ...group,
        contributors: filter(group.contributors, security => get(security, selectedField)),
      }))
      .value()
  },
)

const getSortedGroupContributors = createSelector(
  [getFilteredGroupedContributors, getSelectedField, getSortOrder],
  (data, selectedField, order) => {
    if (data.length === 0) return EMPTY_ARRAY

    return chain(data)
      .map(group => ({
        ...group,
        value: get(group, selectedField) * 100,
        contributors: sortContributors(group.contributors, selectedField, order),
      }))
      .orderBy(...makeGroupSort(order))
      .value()
  },
)

export const getSortedGroupContributorsWithoutCashOffset = createSelector(
  [getSortedGroupContributors],
  groups => map(groups, group => ({
    ...group,
    contributors: filter(group.contributors, contributor => !isCashOffsetContributor(contributor)),
  })),
)

const getAccountRollups = createSelector(
  [getClassifications],
  (classifications) => ({
    classifications,
  }),
)
export const getAvailableRollups = createSelector(
  [getAccountRollups, getSelectedDate, getViewType],
  getAllAvailableRollups,
)

export const getContributorsSummaryText = createSelector(
  [getCurrentGrouping, getSortedContributorsWithoutCashOffset, getSortedGroupContributorsWithoutCashOffset, getSelectedGroup, getShowNotHeld, getViewType],
  positionSetContributorsSummaryText,
)

export const getCsvData = createSelector(
  [getSortedContributors, getSortedGroupContributors, getSelectedContributorCategory, getCurrentGrouping, getPositionSetName, getSelectedDate, getViewType, getSelectedGroup],
  (data, groupedData, contributorCategory, contributorsGrouping, positionSetName, selectedDate, viewType, selectedGroup) => {
    if (selectedGroup) {
      const group = first(groupedData)
      return computeGroupedContributorsCSV({
        type: "Risk",
        portfolioName: positionSetName,
        selected: contributorCategory,
        selectedDate,
        viewType,
        group,
      })
    }

    if (contributorsGrouping) {
      return computeContributorsGroupCSV({
        type: "Risk",
        portfolioName: positionSetName,
        selected: contributorCategory,
        selectedDate,
        viewType,
        contributorsGrouping,
        data: groupedData,
      })
    }

    return computeContributorsCSV({
      type: "Risk",
      portfolioName: positionSetName,
      selected: contributorCategory,
      selectedDate,
      viewType,
      data,
    })
  },
)

export const getSelectedGroupValue = createSelector(
  [getSelectedGroup, getGroupedContributors, getSelectedField],
  (selectedGroup, groupedContributors, selectedField) => {
    if (selectedGroup) {
      const group = find(groupedContributors, { id: selectedGroup.id })
      return get(group, selectedField) * 100
    }

    return null
  },
)

export const getSelectedGroupSiblings = createSelector(
  [getSelectedGroup, getGroupedContributors, getSelectedField],
  (selectedGroup, groupedContributors, selectedField) => {
    if (selectedGroup) {
      return chain(groupedContributors)
        .filter(group => get(group, selectedField))
        .map(group => ({
          ...group,
          value: get(group, selectedField) * 100,
        }))
        .orderBy(["name"], ["asc"])
        .value()
    }

    return null
  },
)

const getIsActive = (_, props) => get(props, "viewType") === VIEW_TYPES.ACTIVE

export const getAttributionMethod = createSelector(
  [getIsActive],
  computeAttributionMethod,
)
